import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import headerLogo from '../../assets/images/Header Logo.png';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';

const HeaderContainer = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background-color: #fff;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  
  img {
    height: 40px; /* Keep original height to maintain layout */
    width: auto;
    margin-right: 10px;
    transform: scale(1); /* Slightly reduced visual size */
    transform-origin: left center; /* Anchor scaling from left side */
  }
  
  .logo-text {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    line-height: 1.1;
    width: 320px; /* fixed width ensures both lines take same space */
  }
  
  .logo-text span {
    display: block;
  }
  
  .logo-text .ar {
    font-size: 30px; /* Further increased for width match */
    font-weight: 700;
    color: #333;
    direction: rtl;
    letter-spacing: 1px;
    white-space: nowrap;
  }
  
  .logo-text .en {
    font-size: 16px;
    font-weight: bold;
    color: #333;
    letter-spacing: 0.2px; /* Slightly tighter */
    white-space: nowrap;
  }
`;

const NavMenu = styled.nav`
  display: flex;
  align-items: center;
  background-color: #7985FF;
  border-radius: 25px;
  padding: 8px 16px;
  gap: 15px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

// Mobile menu button (hamburger icon)
const MobileMenuButton = styled.button`
  background: none;
  border: none;
  color: #4169e1;
  font-size: 24px;
  cursor: pointer;
  display: none;
  z-index: 1000;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

// Mobile menu overlay
const MobileMenu = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: white;
  z-index: 999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-100%)'} ;
  transition: transform 0.3s ease-in-out;
  
  nav {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }
`;

const NavLink = styled.a`
  padding: 8px 15px;
  color: #000000;
  font-weight: 600;  /* Made all navigation text bold */
  position: relative;
  text-decoration: none;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    color: #333333;
  }
`;

const BookNowButton = styled.button`
  background-color: white;
  color: #4169e1;
  padding: 10px 20px;
  border-radius: 25px;
  font-weight: bold;
  border: none;
  font-size: 14px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
  
  &:hover {
    background-color: #f5f5f5;
    transform: translateY(-2px);
  }
`;

function Header() {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isRtl = i18n.language === 'ar';
  
  useEffect(() => {
    // Add smooth scrolling behavior to the document
    document.documentElement.style.scrollBehavior = 'smooth';
    
    return () => {
      // Clean up when component unmounts
      document.documentElement.style.scrollBehavior = '';
    };
  }, []);
  
  return (
    <HeaderContainer>
      <Logo>
        <img 
          src={headerLogo} 
          alt="Header logo" 
          style={isRtl ? { marginRight: '5px' } : {}} 
        />
      </Logo>
      
      {/* Mobile Menu Button */}
      <MobileMenuButton onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
        {mobileMenuOpen ? '✕' : '☰'}
      </MobileMenuButton>
      
      {/* Mobile Menu */}
      <MobileMenu $isOpen={mobileMenuOpen}>
        <nav>
          <NavLink as={Link} to="/" onClick={() => setMobileMenuOpen(false)}>{t('footer.navigation.home')}</NavLink>
          {isRtl ? (
            // Arabic order: Home, About Us, Services, Reviews, Team
            <>
              <NavLink as={Link} to="/about" onClick={() => setMobileMenuOpen(false)}>{t('footer.navigation.about')}</NavLink>
              <NavLink as={Link} to="/services" onClick={() => setMobileMenuOpen(false)}>{t('footer.navigation.services')}</NavLink>
              <NavLink as={Link} to="/reviews" onClick={() => setMobileMenuOpen(false)}>{t('footer.navigation.reviews')}</NavLink>
              <NavLink as={Link} to="/team" onClick={() => setMobileMenuOpen(false)}>{t('footer.navigation.team')}</NavLink>
            </>
          ) : (
            // English order: Home, About Us, Services, Reviews, Our Team
            <>
              <NavLink as={Link} to="/about" onClick={() => setMobileMenuOpen(false)}>{t('footer.navigation.about')}</NavLink>
              <NavLink as={Link} to="/services" onClick={() => setMobileMenuOpen(false)}>{t('footer.navigation.services')}</NavLink>
              <NavLink as={Link} to="/reviews" onClick={() => setMobileMenuOpen(false)}>{t('footer.navigation.reviews')}</NavLink>
              <NavLink as={Link} to="/team" onClick={() => setMobileMenuOpen(false)}>{t('footer.navigation.team')}</NavLink>
            </>
          )}
          <LanguageSwitcher onClick={() => setMobileMenuOpen(false)} />
          <BookNowButton onClick={() => {
            setMobileMenuOpen(false);
            // Open WhatsApp chat
            window.open('https://wa.me/966558507990', '_blank');
          }}>{t('footer.navigation.appointment')}</BookNowButton>
        </nav>
      </MobileMenu>
      
      <NavMenu>
        <NavLink as={Link} to="/">{t('footer.navigation.home')}</NavLink>
        {isRtl ? (
          // Arabic order: Home, About Us, Services, Reviews, Team
          <>
            <NavLink as={Link} to="/about">{t('footer.navigation.about')}</NavLink>
            <NavLink as={Link} to="/services">{t('footer.navigation.services')}</NavLink>
            <NavLink as={Link} to="/reviews">{t('footer.navigation.reviews')}</NavLink>
            <NavLink as={Link} to="/team">{t('footer.navigation.team')}</NavLink>
          </>
        ) : (
          // English order: Home, About Us, Services, Reviews, Our Team
          <>
            <NavLink as={Link} to="/about">{t('footer.navigation.about')}</NavLink>
            <NavLink as={Link} to="/services">{t('footer.navigation.services')}</NavLink>
            <NavLink as={Link} to="/reviews">{t('footer.navigation.reviews')}</NavLink>
            <NavLink as={Link} to="/team">{t('footer.navigation.team')}</NavLink>
          </>
        )}
        <LanguageSwitcher />
        <BookNowButton onClick={() => {
          // Open WhatsApp chat
          window.open('https://wa.me/966558507990', '_blank');
        }}>{t('footer.navigation.appointment')}</BookNowButton>
      </NavMenu>
    </HeaderContainer>
  );
}

export default Header;
